/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { useTheme } from '../../styles/theme';
import { OpenInFullOutlined } from '@mui/icons-material';
import { ExpressionFullscreen } from '@components/Modals/ExpressionFullscreen';
import { alpha, Stack } from '@mui/material';
import { AnchorType } from '@components/Flow';
import {
  jmespathSupport,
  variableHighlighter,
  variableAutocomplete,
  getVariableOptions,
  calculateEditorHeight,
} from './queryLanguage'; // Adjust the path as needed

import {
  EditorState,
  EditorView,
  ViewPlugin,
  ViewUpdate,
  placeholder as insertPlaceholder,
  keymap,
} from '@uiw/react-codemirror';

import { history, redo, undo, historyKeymap } from '@codemirror/commands';

const ExpressionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  #errorMessage {
    font-size: 12px;
    margin-left: 6px;
    color: ${({ theme }) => theme.telnyx.error};
  }
`;

export const ExpressionContainer = styled.div<{ maxWidth?: string; error?: boolean }>`
  border: 1px solid ${({ theme, error }) => (error ? theme.telnyx.error : theme.line.light)};
  border-radius: 6px;
  position: relative;

  max-width: ${({ maxWidth }) => (maxWidth ? maxWidth : undefined)};

  .valid-expression-variable {
    color: ${({ theme }) => theme.primary};
    background-color: ${({ theme }) => alpha(theme.primary, 0.08)};
  }

  .invalid-expression-variable {
    color: ${({ theme }) => theme.telnyx.red};
    background-color: rgb(243, 92, 92, 0.09);
  }

  .warn-expression-variable {
    /* color: ${({ theme }) => theme.primary}; */
    /* background-color: ${({ theme }) => alpha(theme.primary, 0.08)}; */
    color: ${({ theme }) => theme.telnyx.yellow};
    background-color: rgb(243, 92, 92, 0.07);
  }

  .icon-button {
    position: absolute;
    right: 0;
    bottom: 0;
    border-radius: 6px 0 0 0;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    height: 18px;
    width: 18px;
    background-color: ${({ theme }) => theme.background.standard};
    border: 0;
    border-radius: 6px 0 6px 0;
    border-left: 1px solid ${({ theme }) => theme.line.light};
    border-top: 1px solid ${({ theme }) => theme.line.light};
    z-index: 1;

    svg {
      color: ${({ theme }) => theme.text.standard};
      font-size: 11px;
    }

    &:hover {
      cursor: pointer;
      svg {
        color: ${({ theme }) => theme.primary};
      }
    }
  }

  .cm-tooltip,
  .cm-tooltip-autocomplete {
    border-radius: 6px;
    background-color: ${({ theme }) => theme.background.standard};
    color: ${({ theme }) => theme.text.standard};
    border-color: ${({ theme }) => theme.line.light};

    ul {
      border-radius: 6px;
    }

    li {
      background-color: ${({ theme }) => theme.background.standard};
      font-family: 'Inter';
      font-size: 13px;
    }

    & > ul {
      min-width: 320px !important;
    }

    & > ul > li {
      padding: 5px 3px !important;
    }

    [aria-selected='true'] {
      color: ${({ theme, error }) => `${theme.primary} !important`};
      background-color: ${({ theme }) => `${alpha(theme.primary, 0.06)} !important`};
    }
  }

  .cm-content {
    caret-color: ${({ theme }) => theme.text.standard} !important;
  }
`;

interface Props {
  options: any;
  placeholder: string;
  value: string;
  dataEdges?: AnchorType[];
  title?: string;
  description?: string;
  setValue: (value: string) => void;
  hideFullscreen?: boolean;
  minRows?: number;
  maxRows?: number;
  keyProp?: string;
  wrapping?: boolean;
  maxWidth?: string;
  error?: string;
}

export const Expression: React.FC<Props> = ({
  title,
  options,
  placeholder,
  keyProp,
  value,
  setValue,
  dataEdges,
  description,
  hideFullscreen,
  minRows,
  maxRows,
  wrapping = true,
  maxWidth,
  error,
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();

  const [editorView, setEditorView] = useState<EditorView | null>(null);
  const [open, setOpen] = useState<boolean>(false);

  useEffect(() => {
    if (!editorRef.current) return;

    // Dynamic height plugin
    const dynamicHeightPlugin = ViewPlugin.fromClass(
      class {
        constructor(view: EditorView) {
          view.dom.style.height = calculateEditorHeight(view, minRows, maxRows);
        }

        update(update: ViewUpdate) {
          update.view.dom.style.height = calculateEditorHeight(update.view, minRows, maxRows);
        }
      }
    );

    const extensions = [
      jmespathSupport(),
      ...variableHighlighter(options || null),
      ...variableAutocomplete(getVariableOptions(options || null)),
      EditorView.theme({
        '&': {
          padding: '6px',
          paddingTop: '7px',
          paddingBottom: '3px',
          borderRadius: '6px',
          minHeight: '40px',
        },
        '.cm-scroller::-webkit-scrollbar': {
          display: !wrapping ? 'none' : '',
        },
        '.cm-content': {
          caretColor: theme.text.standard,
        },
        '.cm-scroller': {
          overflow: 'auto',
          fontFamily: 'Inter',
          fontSize: '14px',
          color: theme.text.standard,
          whiteSpace: !wrapping ? 'nowrap' : 'pre-wrap',
        },
        '&.cm-focused': { outline: `2px solid ${theme.primary} !important` },
      }),
      dynamicHeightPlugin,
      EditorView.updateListener.of((update) => {
        if (update.docChanged) {
          const docContent = update.state.doc.toString();
          setValue(docContent);
        }
      }),
      insertPlaceholder(placeholder),
      history({
        newGroupDelay: 200,
      }),
      keymap.of([{ key: 'Mod-z', run: undo }, { key: 'Mod-y', run: redo }, ...historyKeymap]),
    ];

    if (wrapping) extensions.push(EditorView.lineWrapping);

    const startState = EditorState.create({ doc: value, extensions });

    const view = new EditorView({
      state: startState,
      parent: editorRef.current,
    });

    setEditorView(view); // Save the editor view for later updates

    return () => {
      view.destroy();
    };
  }, [dataEdges]);

  // Effect to handle external value changes
  useEffect(() => {
    if (!editorView || value === editorView.state.doc.toString()) return;

    editorView.dispatch({
      changes: { from: 0, to: editorView.state.doc.length, insert: value },
    });
  }, [value, keyProp]);

  return (
    <ExpressionWrapper>
      <ExpressionContainer ref={editorRef} maxWidth={maxWidth} error={Boolean(error)}>
        {!hideFullscreen && (
          <button className='icon-button' onClick={() => setOpen(true)}>
            <OpenInFullOutlined />
          </button>
        )}
        <ExpressionFullscreen
          open={open}
          setOpen={setOpen}
          inputState={{
            key: 'expression',
            title: title || '',
            description,
            options,
            placeholder,
            value,
            setValue,
          }}
        />
      </ExpressionContainer>
      {error && (
        <Stack direction='row' spacing={1}>
          <p id='errorMessage'>{error}</p>
        </Stack>
      )}
    </ExpressionWrapper>
  );
};
